import { create } from 'zustand';
import { resolveTheme, type ThemeMode } from '@central/ui';

/**
 * Preferência de tema.
 *
 * Gravada como **string crua**, e não pelo `persist` do zustand: o script
 * inline do `index.html` lê `localStorage['central.theme']` antes da primeira
 * pintura para evitar o flash branco, e ele espera `"dark"` — não o envelope
 * `{"state":{…}}` que o `persist` grava. Trocar isso por `persist` quebraria o
 * anti-flash em silêncio, porque a aplicação continuaria funcionando.
 */

const CHAVE = 'central.theme';

const media = window.matchMedia('(prefers-color-scheme: dark)');

function aplicar(mode: ThemeMode): 'light' | 'dark' {
  const resolvido = resolveTheme(mode, media.matches);
  document.documentElement.classList.toggle('dark', resolvido === 'dark');
  return resolvido;
}

function lerModo(): ThemeMode {
  const bruto = localStorage.getItem(CHAVE);
  return bruto === 'dark' || bruto === 'light' || bruto === 'system' ? bruto : 'system';
}

export const ROTULO_DE_TEMA: Record<ThemeMode, string> = {
  light: 'Claro',
  dark: 'Escuro',
  system: 'Do sistema',
};

interface ThemeState {
  mode: ThemeMode;
  /** O tema que está de fato na tela — `system` já resolvido. */
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()((set, get) => {
  const inicial = lerModo();

  // Só o modo "sistema" acompanha o SO em tempo real; quem escolheu claro ou
  // escuro fez uma escolha explícita, e sobrescrevê-la seria ignorá-la.
  media.addEventListener('change', () => {
    if (get().mode === 'system') set({ resolved: aplicar('system') });
  });

  return {
    mode: inicial,
    resolved: aplicar(inicial),
    setMode(mode) {
      localStorage.setItem(CHAVE, mode);
      set({ mode, resolved: aplicar(mode) });
    },
  };
});
